import moment from 'moment';
import Cookies from 'js-cookie';

import _ from 'lodash';
import api from './api-service';
import EventService from './event-service';
import Aspera from './aspera';
import AsperaOnboardingService from './aspera-onboarding-service';
import Upload from './upload';
import { isBrowserSelection } from './file-selection';

// Whether the HTTPS uploader can actually send this file. Aspera's own dialog
// hands back a path, a name and a size — the bytes stay on disk and never enter
// the page — and FormData rejects anything that is not a Blob outright. Tested
// on the file rather than read from the selection: this is the invariant the
// uploader itself enforces, so it cannot drift from how a selection was marked.
const canUploadOverHttp = (file) => typeof Blob !== 'undefined' && file instanceof Blob;

// Carried on the error so callers can tell this apart from an upload that
// merely failed: nothing about this one is worth retrying, and the only way
// forward is choosing the file again through the browser.
export const ERR_ASPERA_SELECTION_UNAVAILABLE = 'ERR_ASPERA_SELECTION_UNAVAILABLE';

export default function AuthorizedUploadHandler(options, signal=null) {
	let uploadHandler = {
		error: null,
		state: null,
		progress: 0
	};

	const { updateCb, request: uploadRequest } = options;

	function handleError(string, object) {
		uploadHandler = {
			...uploadHandler,
			state: null,
			error: {
				string: 'userFileUpload.error.' + string,
				object: _.pick(object, ['status', 'data'])
			}
		};

		return new Error('error.' + string);
	}

	async function createRequest(options, signal=null) {
		uploadHandler = {
			...uploadHandler,
			error: null,
			state: 'request',
			progress: 0,
			start: 0,
			current: 0,
			duration: 0,
			estimated: 'Calculating...',
			size: 0
		};

		return await api.client.post('/upload-request', options, {
			signal: signal
		})
			.then((res) => res.data)
			.catch((e) => {
				return Promise.reject(handleError(e));
			});
	}

	async function transferFile(file, request) {
		uploadHandler.state = 'transfer';
		let WHITELIST = [
			'key',
			'AWSAccessKeyId',
			'acl',
			'policy',
			'signature',
			'Content-Type'
		];
		let options = {
			fields: _.pickBy({ ...request, ...uploadRequest }, (value, key) => {
				return WHITELIST.indexOf(key) > -1
						|| key.indexOf('x-amz-meta-') > -1;
			}),
			url: request.bucket,
			method: 'POST',
			file: file,
			onUploadProgress: setProgress,
			signal: request.signal
		};

		if(request.language) {
			options.language = request.language;
		}
		let now = new Date().getTime();

		function setProgress(event) {
			let percent = parseInt(100.0 * event.loaded / event.total, 10);
			uploadHandler.title = file?.name;
			uploadHandler.status = 'running';
			uploadHandler.active = true;
			uploadHandler.progress = percent;
			uploadHandler.bytes_written = event.loaded;
			uploadHandler.bytes_expected = file.size;
			uploadHandler.elapsed = moment.duration((new Date().getTime() - now)/1000).humanize();
			uploadHandler.remaining = '-';

			updateCb && updateCb(uploadHandler);
		}

		function getFileUrl() {
			// eslint-disable-next-line no-template-curly-in-string
			return request.bucket + '/' + request.key.replace('${filename}', file.name);
		}

		function destinationName(file) {
			let path = file.name.replace(/\\/g, '\\\\');
			return (path.charAt(0) === '/') ? path.split('/').pop() : path.split('\\\\').pop();
		}

		uploadHandler.size = file.size;
		uploadHandler.start = new Date().getTime();

		if(request.usage === 'slurpee2' && request.transferSpec) {
			// Failover when Aspera doesn't work, and for anything the user picked
			// through the browser — Connect may have come up since they chose it,
			// but that selection can never be handed to Aspera.
			//
			// Readiness is re-read here rather than trusted from selection time: the
			// upload-request round trip sits in between, and inside it the permission
			// can be revoked in site settings or Aspera switched off in another tab.
			// Handing the file to Connect then meets the browser's gate instead of
			// Connect, with the HTTPS path sitting right here. isReady() covers the
			// plugin status too, so it replaces that check rather than joining it.
			if(isBrowserSelection(file) || !(await AsperaOnboardingService.isReady())) {
				// When Aspera is what went away, HTTPS is not a fallback: there is
				// nothing in the page to send. Say that, rather than letting FormData
				// throw a TypeError on the descriptor from somewhere further down, and
				// let them pick the file again now that the browser is the only way it
				// can go.
				if(!canUploadOverHttp(file)) {
					const error = handleError('asperaSelectionUnavailable');
					error.code = ERR_ASPERA_SELECTION_UNAVAILABLE;

					return Promise.reject(error);
				}

				return Upload.upload(options)
					.then(() => {
						uploadHandler.status = 'completed';
						return request.node_url + '/' + request.ingest + '/' + destinationName(file);
					})
					.catch((error) => Promise.reject(handleError('transfer', error)));
			}
			const credentials = JSON.parse(Cookies.get('pixwel.api.credentials'));
			let specs = {
				paths: [
					{
						source: file.name,
						destination: request.ingest + '/' + destinationName(file)
					}
				],
				authentication: 'token',
				remote_user: request.transferSpec.remote_user,
				remote_host: request.transferSpec.remote_host,
				token: request.transferSpec.token,
				direction: 'send',
				create_dir: true,
				target_rate_kbps: 500000,
				resume: 'sparse_checksum',
				fasp_port: 33001,
				ssh_port: 33001,
				cookie: credentials.username
			};

			let url = request.node_url + '/' + request.ingest + '/' + destinationName(file);

			return new Promise((resolve, reject) => {
				const transferResult = Aspera.Connect.startTransfer(specs, { allow_dialogs: false });
				EventService.emit('Connect:TransferStarted', transferResult);

				uploadHandler.active = false;
				uploadHandler.remaining = 'Calculating...';

				let transferUuid = null;
				let pollInterval = null;

				// Function to process transfer data and update progress
				const processTransfer = (transfer) => {
					if(!transfer) {
						return false;
					}

					const destination = _.get(transfer, 'transfer_spec.paths[0].destination', '');
					if(!destination) {
						return false;
					}

					const ingestId = destination.split('/').shift();
					if(ingestId !== request.ingest) {
						return false;
					}

					// Store the UUID for polling
					if(!transferUuid && transfer.uuid) {
						transferUuid = transfer.uuid;
					}

					uploadHandler.active = true;
					uploadHandler.status = transfer.status;
					uploadHandler.title = transfer.title || file.name;
					uploadHandler.bytes_expected = transfer.bytes_expected || file.size;
					uploadHandler.bytes_written = transfer.bytes_written || 0;
					uploadHandler.elapsed = moment.duration((transfer.elapsed_usec || 0)/1000).humanize();
					uploadHandler.remaining = moment.duration((transfer.remaining_usec || 0)/1000).humanize();

					// Calculate progress - prefer bytes calculation for accuracy
					let progress = 0;
					if(uploadHandler.bytes_expected > 0 && uploadHandler.bytes_written >= 0) {
						progress = (uploadHandler.bytes_written / uploadHandler.bytes_expected) * 100;
					}
					else if(transfer.percentage !== null) {
						progress = transfer.percentage * 100;
					}
					uploadHandler.progress = Math.min(100, Math.max(0, progress || 0));

					uploadHandler.abort = () => {
						uploadHandler.cancelling = true;
						if(pollInterval) {
							clearInterval(pollInterval);
						}
						Aspera.Connect.removeTransfer(transfer.uuid);
					};

					updateCb && updateCb(uploadHandler);

					if(transfer.status === 'completed') {
						uploadHandler.active = false;
						if(pollInterval) {
							clearInterval(pollInterval);
						}
						return 'completed';
					}
					if(transfer.status === 'removed' || transfer.status === 'failed') {
						uploadHandler.active = false;
						uploadHandler.cancelling = false;
						if(pollInterval) {
							clearInterval(pollInterval);
						}
						return 'failed';
					}

					return true;
				};

				// Poll for transfer progress every 500ms
				const startPolling = () => {
					pollInterval = setInterval(() => {
						if(!transferUuid) {
							return;
						}

						Aspera.Connect.getAllTransfers()
							.then((allTransfers) => {
								if(allTransfers && allTransfers.transfers) {
									const transfer = allTransfers.transfers.find((t) => t.uuid === transferUuid);
									if(transfer) {
										const result = processTransfer(transfer);
										if(result === 'completed') {
											resolve(url);
										}
										else if(result === 'failed') {
											reject(handleError('transfer', new Error('Transfer failed or removed')));
										}
									}
								}
							})
							.catch((e) => {
								// Polling failed, rely on event listener
								console.error(e);
							});
					}, 500);
				};

				uploadHandler.listener = (event, obj) => {
					obj.transfers.forEach((transfer) => {
						const result = processTransfer(transfer);
						if(result === 'completed') {
							resolve(url);
						}
						else if(result === 'failed') {
							reject(handleError('transfer', new Error('removed')));
						}
					});
				};

				transferResult
					.then((result) => {
						if(result && result.uuid) {
							transferUuid = result.uuid;
						}
						Aspera.Connect.addEventListener('transfer', uploadHandler.listener);
						// Start polling after a short delay to allow transfer to initialize
						setTimeout(startPolling, 100);
					})
					.catch((error) => reject(handleError('transfer', error)));
			});

		}

		return Upload.upload(options)
			.then(getFileUrl)
			.catch((error) => {
				if( error.code === 'ERR_CANCELED' ) {
					return Promise.reject(error);
				}
				return Promise.reject(handleError('transfer', error));
			});

	}

	function finalize(result) {
		uploadHandler.state = 'finished';
		return result;
	}

	let promise = createRequest(options.request, signal)
		.then((req) => transferFile(options.file, { ...req, ...options.request, signal }))
		.then((result) => {
			return Promise.resolve(finalize(result));
		})
		.catch((error) => {
			// A cancellation and a lost Aspera selection each already say what
			// happened, and neither is an upload that failed. Renaming them to a
			// generic failure would throw away the only thing a caller can act on.
			if( error.code === 'ERR_CANCELED' || error.code === ERR_ASPERA_SELECTION_UNAVAILABLE ) {
				return Promise.reject(error);
			}
			return Promise.reject(handleError('Upload', error));
		});

	return Object.assign(promise, { uploadHandler });
}
